import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import {
  Gear, Buildings, Storefront, CurrencyInr, Receipt as ReceiptIcon, UsersThree, CreditCard,
  WhatsappLogo, ChatCircleDots, EnvelopeSimple, Globe, Megaphone, MagnifyingGlass, HardDrives,
  ClipboardText, PlugsConnected, ShieldCheck, ArrowRight, Rocket, WarningCircle, Sparkle,
} from "@phosphor-icons/react";

const Database = HardDrives;

// Sections mirror the "Professional SaaS Settings Menu" spec.
const SECTIONS = [
  {
    id: "general",
    title: "General",
    hint: "Business identity, timezone, currency defaults",
    items: [
      { key: "business",  label: "Business Profile",  icon: Storefront, to: "/settings/business",     available: true, hint: "Legal name · GSTIN · logo · signature" },
      { key: "general",   label: "General Settings",  icon: Gear,       to: "/settings/general",      available: true, hint: "Language · currency · date format" },
    ],
  },
  {
    id: "business",
    title: "Business",
    hint: "Branches, service catalogue, pricing & taxes",
    items: [
      { key: "branches",   label: "Branches",     icon: Buildings,    to: "/branches-admin", available: true, hint: "Multi-branch management + RBAC" },
      { key: "services",   label: "Services",     icon: ReceiptIcon,  to: "/service-rates",  available: true, hint: "Service catalogue and workflow" },
      { key: "pricing",    label: "Pricing",      icon: CurrencyInr,  to: "/service-rates",  available: true, hint: "Government fee + service charge + GST" },
      { key: "taxes",      label: "Taxes & GST",  icon: ReceiptIcon,  to: "/settings/taxes", available: true, hint: "GST slab · HSN · Cess rules" },
    ],
  },
  {
    id: "users",
    title: "Users & Roles",
    hint: "Staff accounts, permissions, audit trail",
    items: [
      { key: "users",  label: "Users & Roles", icon: UsersThree,     to: "/staff",       available: true, hint: "Staff, branch admins, super admin" },
      { key: "audit",  label: "Audit Logs",    icon: ClipboardText,  to: "/audit-logs",  available: true, hint: "Every action, every user, timestamped" },
      { key: "security", label: "Security",    icon: ShieldCheck,    to: "/settings/security", available: true, hint: "2FA · IP allow-list · password policy" },
    ],
  },
  {
    id: "payments",
    title: "Payments",
    hint: "Collections, gateways, reconciliation",
    items: [
      { key: "payment_center", label: "Payment Center", icon: CreditCard,   to: "/payment-center", available: true, hint: "UPI · Cards · Wallets · Cash" },
      { key: "collections",    label: "Collections",    icon: CurrencyInr,  to: "/collections",    available: true, hint: "Advance tracking · dues · reminders" },
      { key: "razorpay",       label: "Razorpay",       icon: CreditCard,   to: "/settings#razorpay", available: false, hint: "Add API key to go live" },
    ],
  },
  {
    id: "comms",
    title: "Communication",
    hint: "WhatsApp, SMS, and email settings",
    items: [
      { key: "whatsapp", label: "WhatsApp Center", icon: WhatsappLogo,     to: "/whatsapp-center",  available: true, hint: "Send bills, updates, receipts" },
      { key: "sms",      label: "SMS",             icon: ChatCircleDots,   to: "/settings/sms",     available: true, hint: "MSG91 · Twilio · Fast2SMS" },
      { key: "email",    label: "Email",           icon: EnvelopeSimple,   to: "/settings/email",   available: true, hint: "SendGrid · Resend · SMTP" },
    ],
  },
  {
    id: "website",
    title: "Website & Marketing",
    hint: "Public site, ads, SEO",
    items: [
      { key: "website",  label: "Website Settings",     icon: Globe,           to: "/settings/website", available: true, hint: "Hero copy · service showcase · reviews" },
      { key: "ads",      label: "Advertisement Manager", icon: Megaphone,      to: "/marketing",         available: true, hint: "Meta / Google Ads · budget · ROI" },
      { key: "seo",      label: "SEO",                   icon: MagnifyingGlass, to: "/settings/seo",      available: true, hint: "Meta tags · sitemap · schema" },
    ],
  },
  {
    id: "system",
    title: "System",
    hint: "Backups, logs, integrations, recovery",
    items: [
      { key: "backup",   label: "Backup & Restore",   icon: Database,          to: "/go-live",          available: true, hint: "Encrypted .enc backups · restore" },
      { key: "logs",     label: "System Logs",        icon: ClipboardText,     to: "/audit-logs",       available: true, hint: "System + audit activity" },
      { key: "apis",     label: "API Integrations",   icon: PlugsConnected,    to: "/gov-portals",      available: true, hint: "Gov portals · WhatsApp · Razorpay" },
      { key: "launch",   label: "🚀 Launch Portal",   icon: Rocket,            to: "/go-live",          available: true, hint: "Publish live · switch from demo",     highlight: "primary" },
      { key: "recovery", label: "⚠️ System Recovery", icon: WarningCircle,     to: "/go-live",          available: true, hint: "Reset demo data · factory reset",     highlight: "danger" },
    ],
  },
];

export default function Settings() {
  const nav = useNavigate();
  const totalAvailable = SECTIONS.reduce((n, s) => n + s.items.filter((i) => i.available).length, 0);
  const totalUpcoming  = SECTIONS.reduce((n, s) => n + s.items.filter((i) => !i.available).length, 0);

  return (
    <div className="max-w-[1400px] mx-auto p-4 lg:p-6 space-y-8" data-testid="settings-hub">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Sparkle size={14} weight="fill" className="text-orange-500" />
            <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-slate-500">Configuration Hub</span>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Settings</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            One place to configure DigitalSetu — business profile, branches, users, payments &amp; more.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 font-semibold">{totalAvailable} live</span>
          <span className="px-3 py-1.5 rounded-full bg-slate-100 text-slate-700 font-semibold">{totalUpcoming} coming soon</span>
        </div>
      </div>

      {/* Sections */}
      {SECTIONS.map((sec) => (
        <div key={sec.id} data-testid={`section-${sec.id}`}>
          <div className="mb-3">
            <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>{sec.title}</h2>
            <p className="text-xs text-slate-500">{sec.hint}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {sec.items.map((it) => {
              const Ic = it.icon;
              const primary = it.highlight === "primary";
              const danger  = it.highlight === "danger";
              const clickable = it.available;
              const cardCls = `p-5 border transition-all cursor-pointer ${primary ? "bg-gradient-to-br from-orange-500 to-orange-600 text-white border-orange-500 shadow-md hover:shadow-lg"
                : danger ? "bg-red-50 border-red-300 hover:border-red-500"
                : "bg-white border-slate-200 hover:border-[#1e3a8a] hover:shadow-md"} ${!clickable ? "opacity-70 cursor-not-allowed" : ""}`;
              const iconTone = primary ? "text-white" : danger ? "text-red-600" : "text-[#1e3a8a]";
              const iconBg   = primary ? "bg-white/20" : danger ? "bg-red-100" : "bg-blue-50";
              return (
                <Card
                  key={it.key}
                  data-testid={`setting-${it.key}`}
                  className={cardCls}
                  onClick={() => clickable && nav(it.to)}
                  role={clickable ? "button" : undefined}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center`}>
                      <Ic size={20} weight={primary || danger ? "fill" : "duotone"} className={iconTone} />
                    </div>
                    {!it.available && (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">Soon</span>
                    )}
                  </div>
                  <div className={`font-semibold ${primary ? "text-white" : "text-slate-900"}`}>{it.label}</div>
                  <div className={`text-[11px] mt-0.5 ${primary ? "text-orange-100" : "text-slate-500"}`}>{it.hint}</div>
                  {it.available && (
                    <div className={`inline-flex items-center gap-1 text-xs font-semibold mt-3 ${primary ? "text-white" : "text-[#1e3a8a]"}`}>
                      Open <ArrowRight size={11} />
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
